import { BusinessForm, BusinessFormType, ContractStatus, ContractStatusType, PositionNumberType } from 'api/common/constants'
import { LoginResponse } from 'api/common/responses'
import { PositionContractSelectedData } from 'api/common/types'
import { toBoolean, toNumber } from 'utilities/typeUtility'

/**
 * ポジション購入情報
 */
export interface PurchasePositionData {
  positionNumbers: PositionNumberType[]
  amount: number
}

const tokenKey = 'login-token'
const contractStatusKey = 'contract-status'
const registrationStatusKey = 'registration-status'
const userTypeKey = 'user-type'
const reservedPositionCountKey = 'reserved-position-count'
const positionContractSelectedDataKey = 'position-contract-selected-data'
const purchasePositionDataKey = 'purchase-position-data'

/**
 * ログインデータ管理
 */
class LoginData {
  private token: string | null
  private contractStatus?: ContractStatusType
  private registrationStatus?: boolean
  private userType?: BusinessFormType
  private reservedPositionCount?: number
  private positionContractSelectedData?: PositionContractSelectedData
  private purchasePositionData?: PurchasePositionData

  constructor() {
    this.token = localStorage.getItem(tokenKey)
    this.registrationStatus = toBoolean(localStorage.getItem(registrationStatusKey))
    const contractStatusString = localStorage.getItem(contractStatusKey)
    if (contractStatusString != null) {
      this.contractStatus = Number(contractStatusString) as ContractStatusType
    }
    const userTypeString = localStorage.getItem(userTypeKey)
    if (userTypeString != null) {
      this.userType = Number(userTypeString) as BusinessFormType
    }
    this.reservedPositionCount = toNumber(localStorage.getItem(reservedPositionCountKey))
    const positionContractSelectedDataString = localStorage.getItem(positionContractSelectedDataKey)
    if (positionContractSelectedDataString != null) {
      this.positionContractSelectedData = JSON.parse(positionContractSelectedDataString)
    }
    const purchasePositionDataString = localStorage.getItem(purchasePositionDataKey)
    if (purchasePositionDataString != null) {
      this.purchasePositionData = JSON.parse(purchasePositionDataString)
    }
  }

  public getToken(): string | null {
    return this.token
  }

  public getContractStatus() {
    return this.contractStatus
  }

  public getRegistrationStatus() {
    return this.registrationStatus
  }

  public getUserType() {
    return this.userType
  }

  public getReservedPositionCount() {
    return this.reservedPositionCount
  }

  public getPositionContractSelectedData() {
    return this.positionContractSelectedData
  }

  public getPurchasePositionData() {
    return this.purchasePositionData
  }

  /**
   * ログイン中であるか
   * @returns
   */
  public isLoggedIn(): boolean {
    return this.token != null
  }

  /**
   * お申込みが完了しているか
   * - 「お申込み・承諾登録済み」まで進んでるとお申込みが完了している
   * @returns
   */
  public isRegistrationCompleted(): boolean {
    switch (this.contractStatus) {
      case ContractStatus.InitialRegistration:
      case ContractStatus.EmailAddressApproved:
      case ContractStatus.SummaryDocumentDelivered:
      case ContractStatus.CorporateDocumentRejected: {
        return false
      }
      default: {
        return true
      }
    }
  }

  /**
   * 事業形態が法人である
   * @returns
   */
  public isCompany(): boolean {
    return this.userType === BusinessForm.Company
  }

  /**
   * キャンペーン仮登録のみ完了で住所・口座情報（本登録 13 項目）が未入力の状態か
   *
   * true の場合、本人確認・ポジション選択・ポジション契約（クレジット決済を含む）画面への
   * アクセスは禁止し /myPage にリダイレクトする。住所・口座入力後（contractStatus が
   * ApplicationAndAgreementRegistered 以上に進行）は false を返す。
   */
  public isCampaignAwaitingFullRegistration(): boolean {
    return this.contractStatus === ContractStatus.CampaignRegistered
  }

  /**
   * ナビゲーションメニューを表示できるか
   * @returns
   */
  public canUseNavMenu(): boolean {
    return (
      this.isLoggedIn() &&
      (this.contractStatus === ContractStatus.Completed || this.contractStatus === ContractStatus.CampaignRegistered)
    )
  }

  /**
   * ログイン後の遷移先パスの取得
   * @returns
   */
  public getLoggedInScene(): string {
    if (this.isLoggedIn()) {
      // BPO審査が進行中の場合、BPO審査画面にリダイレクト
      const bpoDataModule = require('./bpoData')
      const bpoData = bpoDataModule.default
      if (bpoData.isBpoInProgress()) {
        return '/bpo/processing'
      }
      
      switch (this.contractStatus) {
        case ContractStatus.Completed: {
          return '/myPage'
        }
        case ContractStatus.CampaignRegistered: {
          // キャンペーン QR 経由 verify 完了ユーザーは BE Stage B で
          // Position×3 自動作成済のため PositionSelect / 決済を経由しない。
          // 暫定: 専用 LP 整備までは myPage に着地。
          return '/myPage'
        }
        case ContractStatus.CorporateDocumentRegistrationPending: {
          return '/registerIdentificationForCompanyComplete'
        }
        case ContractStatus.CorporateDocumentRejected: {
          return '/connectorEdit'
        }
        default: {
          if (this.positionContractSelectedData) {
            return this.getPositionSelectNextScene()
          } else {
            return '/positionSelect'
          }
        }
      }
    } else {
      return '/'
    }
  }

  /**
   * ポジション選択後の遷移先パスの取得
   * @returns
   */
  public getPositionSelectNextScene(): string {
    switch (this.contractStatus) {
      case ContractStatus.InitialRegistration:
      case ContractStatus.EmailAddressApproved:
      case ContractStatus.SummaryDocumentDelivered: {
        return this.registrationStatus ? '/connectorEdit' : '/connectorSubscription'
      }
      case ContractStatus.CorporateDocumentRejected: {
        return '/connectorEdit'
      }
      case ContractStatus.ApplicationAndAgreementRegistered: {
        return this.userType === BusinessForm.Individual ? '/registerIdentificationForIndividual' : '/registerIdentificationForCompany'
      }
      case ContractStatus.PersonalDocumentRegistered: {
        return this.userType === BusinessForm.Individual ? '/positionContract' : '/registerIdentificationForCompany'
      }
      default: {
        return '/positionContract'
      }
    }
  }

  /**
   * 証明書登録の登録状況
   * @returns true: 登録済み, false: 登録段階ではない, null: 未登録
   */
  public getIdentificationRegistrationStatus(): boolean | null {
    switch (this.contractStatus) {
      case ContractStatus.InitialRegistration:
      case ContractStatus.EmailAddressApproved:
      case ContractStatus.SummaryDocumentDelivered: {
        return false
      }
      case ContractStatus.ApplicationAndAgreementRegistered: {
        return null
      }
      default: {
        return true
      }
    }
  }

  public setToken(token: string): void {
    this.token = token
    localStorage.setItem(tokenKey, token)
  }

  public setContractStatus(contractStatus: ContractStatusType): void {
    this.contractStatus = contractStatus
    localStorage.setItem(contractStatusKey, String(contractStatus))
  }

  public setRegistrationStatus(registrationStatus: boolean): void {
    this.registrationStatus = registrationStatus
    localStorage.setItem(registrationStatusKey, String(registrationStatus))
  }

  public setUserType(userType: BusinessFormType): void {
    this.userType = userType
    localStorage.setItem(userTypeKey, String(userType))
  }

  public setReservedPositionCount(reservedPositionCount: number): void {
    this.reservedPositionCount = reservedPositionCount
    localStorage.setItem(reservedPositionCountKey, String(reservedPositionCount))
  }

  public setPositionContractSelectedData(positionContractSelectedData: PositionContractSelectedData): void {
    this.positionContractSelectedData = positionContractSelectedData
    localStorage.setItem(positionContractSelectedDataKey, JSON.stringify(positionContractSelectedData))
  }

  public setPurchasePositionData(purchasePositionData: PurchasePositionData): void {
    this.purchasePositionData = purchasePositionData
    localStorage.setItem(purchasePositionDataKey, JSON.stringify(purchasePositionData))
  }

  public resetPurchaseData(): void {
    this.positionContractSelectedData = undefined
    this.purchasePositionData = undefined
    localStorage.removeItem(positionContractSelectedDataKey)
    localStorage.removeItem(purchasePositionDataKey)
  }

  /**
   * ログインAPIのレスポンスからログインデータをセット
   * @param loginResponse
   */
  public async setResponse(loginResponse: LoginResponse): Promise<void> {
    this.setToken(loginResponse.token)
    this.setContractStatus(loginResponse.contractStatus)
    this.setRegistrationStatus(loginResponse.registrationStatus)
    this.setUserType(loginResponse.userType)
    this.setReservedPositionCount(loginResponse.reservedPositionCount)
    this.resetPurchaseData()
    if (loginResponse.selectedPositionData) {
      this.setPositionContractSelectedData(loginResponse.selectedPositionData)
    }
    
    // BPO状態を最新化
    await this.refreshBpoStatus()
  }
  
  /**
   * BPO状態を最新化する
   */
  private async refreshBpoStatus(): Promise<void> {
    const bpoDataModule = require('./bpoData')
    const bpoData = bpoDataModule.default
    const requestId = bpoData.getBpoRequestId()
    
    if (requestId) {
      try {
        const { getBpoStatusAPI } = require('api/bpo')
        const response = await getBpoStatusAPI(requestId)
        
        if (response && response.status) {
          // 最新の状態でセッションストレージを更新
          bpoData.setBpoStatus(response.status)
          
          // 完了している場合はクリア
          if (response.status === 'approved' || response.status === 'rejected' || response.status === 'error' || response.status === 'timeout') {
            bpoData.clearAll()
          }
        }
      } catch (error: any) {
        // 404エラーの場合はBPOデータが存在しないのでクリア
        if (error?.response?.status === 404) {
          bpoData.clearAll()
        }
        // その他のエラーは無視（ネットワークエラーなど）
      }
    }
  }

  /**
   * ログインデータをクリア
   */
  public clear(): void {
    this.token = null
    this.contractStatus = undefined
    this.registrationStatus = undefined
    this.userType = undefined
    this.reservedPositionCount = undefined
    this.positionContractSelectedData = undefined
    this.purchasePositionData = undefined
    localStorage.clear()
  }
}

const loginData = new LoginData()
export default loginData
